/**
 * Sidebar model: sections of channel / DM / agent rows with names, unread state and presence, sorted — plus my own
 * organization (per user, synced): folders holding channels, DMs and docs items, the Starred section, manual order
 * (fractional-indexing ranks), and the placement writes for drag and drop and "Move to folder…".
 */
import type {
  AgentState,
  Channel,
  ChannelMembership,
  Member,
  PageNode,
  SidebarFolder,
  SidebarItem,
  SidebarItemKind,
  SidebarMove,
  UpdateMembershipRequest,
  UpdateSidebarFolderRequest,
  UserStatus,
} from "@bundle/contracts";
import { generateKeyBetween, generateNKeysBetween } from "fractional-indexing";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { isExternal, organizationName } from "./access.ts";
import { memberPaused } from "./dnd.ts";
import { activeStatus } from "./profile.ts";
import { firstName, isClosed } from "./directMessages.ts";
import { agentState, type LiveState } from "./live.ts";
import { channelUnread, setMembership, setSidebarItem, upsertSidebarFolder, type WorkspaceCache } from "./workspace.ts";

export type AvatarPresence = "online" | "away";

export type ChannelRow = {
  type: "channel";
  id: string;
  channelId: string;
  name: string;
  isPrivate: boolean;
  external: boolean;
  muted: boolean;
  unread: boolean;
  mentions: number;
};

export type DmRow = {
  type: "dm";
  id: string;
  channelId: string;
  name: string;
  /** Other participants (empty for my self-DM). */
  userIds: string[];
  avatarName: string;
  avatarUrl?: string;
  presence?: AvatarPresence;
  /** The other person (a 1:1 DM) has Do not disturb on: a moon instead of the presence dot. */
  dnd?: boolean;
  /** The other person's (or, in my self-DM, my) current custom status; never on group DMs (logic/profile activeStatus). */
  status?: UserStatus;
  isGroup: boolean;
  isAgent: boolean;
  external: boolean;
  organization?: string;
  unread: boolean;
  mentions: number;
};

export type AgentRow = {
  type: "agent";
  id: string;
  userId: string;
  name: string;
  avatarUrl?: string;
  state: AgentState;
  activity?: string;
  presence: AvatarPresence;
  /** Existing 1:1 DM with this agent, if any. */
  channelId?: string;
  unread: boolean;
  mentions: number;
};

/** A docs item (page, database, board) I put in one of my folders. */
export type PageRow = {
  type: "page";
  id: string;
  pageId: string;
  /** "" = untitled. */
  name: string;
  pageKind: "page" | "database";
  /** The page's own icon (emoji, or "icon:<name>"). */
  icon?: string;
};

export type SidebarRow = ChannelRow | DmRow | AgentRow;
/** Anything a section lists: conversation and agent rows, and docs items in my folders. */
export type SidebarEntry = SidebarRow | PageRow;
/** The built-in sections. */
export type SidebarSectionId = "starred" | "channels" | "dms" | "agents";
/**
 * `id`: a built-in SidebarSectionId, or "f:<folderId>" for one of my folders (which also carry `folderId` and their
 * label `emoji` or `icon`).
 */
export type SidebarSection = {
  id: string;
  title: MessageDescriptor;
  rows: SidebarEntry[];
  folderId?: string;
  emoji?: string;
  icon?: string;
  /** Direct messages: how many quieter conversations are left out (a "More conversations…" row opens Browse DMs). */
  more?: number;
};

/** Direct messages lists conversations with activity in this many days… */
export const DM_RECENT_DAYS = 14;
/** …at most this many of them (plus unread, closed-but-open and open ones; Slack trims the same way). */
export const DM_RECENT_MAX = 15;

/** What else shapes the sidebar: `open` = conversations open in a tab or pane, or on screen (always listed). */
export type SidebarOptions = { open?: readonly string[] };

function cmpText(a: string, b: string): number {
  const x = a.toLowerCase();
  const y = b.toLowerCase();
  return x < y ? -1 : x > y ? 1 : 0;
}

function presenceOf(live: LiveState, userId: string): AvatarPresence | undefined {
  const p = live.presence[userId];
  return p === "active" ? "online" : p === "away" ? "away" : undefined;
}

export function agentPresence(s: AgentState): AvatarPresence {
  return s === "offline" ? "away" : "online";
}

function isDm(c: Channel): boolean {
  return c.kind === "dm" || c.kind === "group_dm";
}

function otherIds(state: WorkspaceCache, c: Channel): string[] {
  return (c.dmUserIds ?? []).filter((u) => u !== state.meId);
}

/**
 * Display name of a DM: the other person ("Bo Chen"), the others' first names for a group DM ("Bo, Camille, Diego"),
 * or my own name for a self-DM.
 */
export function dmName(state: WorkspaceCache, c: Channel): string {
  const ids = otherIds(state, c);
  const me = state.members[state.meId];
  if (ids.length === 0) return me === undefined ? "" : me.displayName;
  const names = ids.map((id) => {
    const m = state.members[id];
    return m === undefined ? id : m.displayName;
  });
  if (names.length === 1) return names[0]!;
  return names.map(firstName).join(", ");
}

/** Channel title for headers/tabs: "design" for channels, participants for DMs. */
export function channelTitle(state: WorkspaceCache, channelId: string): string {
  const c = state.channels[channelId];
  if (c === undefined) return "";
  return isDm(c) ? dmName(state, c) : c.name;
}

function channelRow(state: WorkspaceCache, c: Channel): ChannelRow {
  const u = channelUnread(state, c.id);
  const ms = state.memberships[c.id];
  return {
    type: "channel",
    id: `c:${c.id}`,
    channelId: c.id,
    name: c.name,
    isPrivate: c.kind === "private",
    external: c.external === true,
    muted: ms !== undefined && ms.muted,
    unread: u.unread,
    mentions: u.mentions,
  };
}

function dmRow(state: WorkspaceCache, live: LiveState, c: Channel, now: number | undefined): DmRow {
  const u = channelUnread(state, c.id);
  const ids = otherIds(state, c);
  const first: Member | undefined = ids.length === 1 ? state.members[ids[0]!] : ids.length === 0 ? state.members[state.meId] : undefined;
  const isAgent = first !== undefined && ids.length === 1 && first.kind === "agent";
  const row: DmRow = {
    type: "dm",
    id: `c:${c.id}`,
    channelId: c.id,
    name: dmName(state, c),
    userIds: ids,
    avatarName: first === undefined ? dmName(state, c) : first.displayName,
    isGroup: ids.length > 1,
    isAgent,
    external: ids.some((id) => {
      const m = state.members[id];
      return m !== undefined && isExternal(m);
    }),
    unread: u.unread,
    mentions: u.mentions,
  };
  if (first !== undefined && first.avatarUrl !== undefined) row.avatarUrl = first.avatarUrl;
  if (first !== undefined && ids.length === 1) {
    const p = isAgent ? agentPresence(agentState(live, first).state) : presenceOf(live, first.userId);
    if (p !== undefined) row.presence = p;
    if (!isAgent && now !== undefined && memberPaused(first, now)) row.dnd = true;
    const st = now === undefined ? undefined : activeStatus(first, now);
    if (st !== undefined) row.status = st;
    const org = organizationName(first);
    if (org !== undefined) row.organization = org;
  } else if (first !== undefined && ids.length === 0) {
    const p = presenceOf(live, first.userId);
    if (p !== undefined) row.presence = p;
    const st = now === undefined ? undefined : activeStatus(first, now);
    if (st !== undefined) row.status = st;
  }
  return row;
}

function agentRow(state: WorkspaceCache, live: LiveState, m: Member): AgentRow {
  const live1 = agentState(live, m);
  const st: AgentState = live1.state;
  const dm = Object.keys(state.memberships)
    .map((id) => state.channels[id])
    .find((c) => c !== undefined && c.kind === "dm" && otherIds(state, c).length === 1 && otherIds(state, c)[0] === m.userId);
  const u = dm === undefined ? { unread: false, mentions: 0 } : channelUnread(state, dm.id);
  const row: AgentRow = {
    type: "agent",
    id: `a:${m.userId}`,
    userId: m.userId,
    name: m.displayName,
    state: st,
    presence: agentPresence(st),
    unread: u.unread,
    mentions: u.mentions,
  };
  if (m.avatarUrl !== undefined) row.avatarUrl = m.avatarUrl;
  if (live1.activity !== undefined) row.activity = live1.activity;
  if (dm !== undefined) row.channelId = dm.id;
  return row;
}

function rowName(r: SidebarEntry): string {
  return r.name;
}

/** Fractional-indexing keys compare by code unit (case matters: "Zz" < "a0"). */
function cmpRank(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** A row with what orders it inside its section. */
type Placed = { row: ChannelRow | DmRow | PageRow; rank: string | undefined; at: number };

/** Ranked rows first (rank, then id), then the rest by `rest` (default order of the section). */
function sortPlaced(list: Placed[], rest: (a: Placed, b: Placed) => number): SidebarEntry[] {
  return list
    .toSorted((a, b) => {
      if (a.rank !== undefined && b.rank !== undefined) return cmpRank(a.rank, b.rank) || cmpRank(a.row.id, b.row.id);
      if (a.rank !== undefined) return -1;
      if (b.rank !== undefined) return 1;
      return rest(a, b);
    })
    .map((p) => p.row);
}

const byName = (a: Placed, b: Placed) => cmpText(rowName(a.row), rowName(b.row)) || cmpText(a.row.id, b.row.id);
const byRecent = (a: Placed, b: Placed) => b.at - a.at || cmpText(a.row.name, b.row.name) || cmpText(a.row.id, b.row.id);

/** My folders in order (rank, then id). */
export function folders(state: WorkspaceCache): SidebarFolder[] {
  const all = state.sidebarFolders ?? {};
  return Object.keys(all)
    .map((id) => all[id]!)
    .toSorted((a, b) => cmpRank(a.rank, b.rank) || cmpRank(a.id, b.id));
}

/** Section id of my folder `folderId` in `SidebarSection.id` form. */
/**
 * A collapsed section still lists its unread conversations (bold, or with a mention badge), like Slack; the UI adds
 * the one on screen. Pages in folders never show while collapsed.
 */
export function showsWhenCollapsed(row: SidebarEntry): boolean {
  return row.type !== "page" && (row.unread || row.mentions > 0);
}

export function folderKey(folderId: string): string {
  return `f:${folderId}`;
}

function pageRow(p: PageNode): PageRow {
  const row: PageRow = { type: "page", id: `p:${p.id}`, pageId: p.id, name: p.title, pageKind: p.kind === "database" ? "database" : "page" };
  if (p.icon !== undefined) row.icon = p.icon;
  return row;
}

/**
 * Sections, in order: Starred (only if any), my folders (in their order, also when empty), Channels, Direct messages,
 * Agents (only if any). Inside Starred, folders and Channels, rows I ordered by hand (`rank`) come first, then the rest
 * by name; Direct messages always go by most recent activity, then name; agents by name. 1:1 DMs with agents are
 * listed under Agents unless starred or in a folder. Archived channels are hidden. `pages`: the docs items I can see
 * (the page tree), for the ones I put in folders.
 */
export function buildSidebar(state: WorkspaceCache, live: LiveState, pages?: Record<string, PageNode>, now?: number, options?: SidebarOptions): SidebarSection[] {
  const starred: Placed[] = [];
  const channels: Placed[] = [];
  const dms: Placed[] = [];
  const mine = folders(state);
  const inFolder: Record<string, Placed[]> = {};
  for (const f of mine) inFolder[f.id] = [];
  const items = state.sidebarItems ?? {};
  for (const id of Object.keys(state.memberships)) {
    const c = state.channels[id];
    const ms = state.memberships[id]!;
    if (c === undefined || c.archived) continue;
    const row = isDm(c) ? dmRow(state, live, c, now) : channelRow(state, c);
    const item = items[id];
    const placed: Placed = { row, rank: item?.rank, at: c.lastMessageAt ?? c.createdAt };
    const folder = item?.folderId === undefined ? undefined : inFolder[item.folderId];
    if (ms.starred) starred.push(placed);
    else if (folder !== undefined) folder.push(placed);
    else if (row.type === "channel") channels.push(placed);
    else if (!row.isAgent) dms.push({ ...placed, rank: undefined });
  }
  for (const id of Object.keys(items)) {
    const item = items[id]!;
    const page = pages?.[id];
    const folder = item.folderId === undefined ? undefined : inFolder[item.folderId];
    if (item.kind !== "page" || page === undefined || page.archivedAt !== undefined || page.kind === "row" || folder === undefined) continue;
    folder.push({ row: pageRow(page), rank: item.rank, at: page.updatedAt });
  }

  const agents: AgentRow[] = [];
  for (const id of Object.keys(state.members)) {
    const m = state.members[id]!;
    if (m.kind === "agent" && !m.deactivated && m.userId !== state.meId) agents.push(agentRow(state, live, m));
  }
  agents.sort((a, b) => cmpText(a.name, b.name) || cmpText(a.id, b.id));

  const sections: SidebarSection[] = [];
  if (starred.length > 0) sections.push({ id: "starred", title: msg("nav.starred"), rows: sortPlaced(starred, byName) });
  for (const f of mine) {
    const section: SidebarSection = { id: folderKey(f.id), title: msg("sidebar.folder.name", { name: f.name }), rows: sortPlaced(inFolder[f.id]!, byName), folderId: f.id };
    if (f.emoji !== undefined && f.emoji !== "") section.emoji = f.emoji;
    if (f.icon !== undefined && f.icon !== "") section.icon = f.icon;
    sections.push(section);
  }
  sections.push({ id: "channels", title: msg("nav.channels"), rows: sortPlaced(channels, byName) });
  sections.push(dmSection(state, dms, now, options?.open ?? []));
  if (agents.length > 0) sections.push({ id: "agents", title: msg("nav.agents"), rows: agents });
  return sections;
}

/**
 * Direct messages, trimmed like Slack: conversations I closed are left out until someone posts again; of the rest,
 * those with activity in the last DM_RECENT_DAYS days (the DM_RECENT_MAX most recent) are listed, plus any unread one
 * and any open in a tab or pane. Without `now`, nothing is trimmed by age. `more` counts the ones left out.
 */
function dmSection(state: WorkspaceCache, dms: Placed[], now: number | undefined, open: readonly string[]): SidebarSection {
  const sorted = dms.toSorted(byRecent);
  const rows: SidebarEntry[] = [];
  let recent = 0;
  let more = 0;
  for (const p of sorted) {
    const row = p.row as DmRow;
    const pinned = row.unread || row.mentions > 0 || open.includes(row.channelId);
    const closed = isClosed(state, row.channelId);
    const fresh = !closed && (now === undefined || (p.at >= now - DM_RECENT_DAYS * DAY_MS && recent < DM_RECENT_MAX));
    if (fresh && !pinned) recent++;
    if (pinned || fresh) rows.push(row);
    else more++;
  }
  const section: SidebarSection = { id: "dms", title: msg("nav.directMessages"), rows };
  if (more > 0) section.more = more;
  return section;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Docs items I put in folders (the page tree leaves them out of its own sections). */
export function pagesInFolders(state: WorkspaceCache): string[] {
  const items = state.sidebarItems ?? {};
  const mine = state.sidebarFolders ?? {};
  return Object.keys(items)
    .filter((id) => {
      const it = items[id]!;
      return it.kind === "page" && it.folderId !== undefined && mine[it.folderId] !== undefined;
    })
    .toSorted();
}

// ---------- Moving items between sections (drag and drop, "Move to folder…") ----------

/** Pseudo-section of the docs page tree: a docs item dropped "there" leaves its folder. */
export const DOCS_TREE = "docs";

/** Which rows a section takes: Channels only channels, Direct messages only DMs, Starred both, folders anything. */
export function sectionAccepts(sectionKey: string, rowType: string): boolean {
  if (sectionKey.startsWith("f:")) return rowType === "channel" || rowType === "dm" || rowType === "page";
  if (rowType === "page") return sectionKey === DOCS_TREE;
  if (rowType !== "channel" && rowType !== "dm") return false;
  if (sectionKey === "channels") return rowType === "channel";
  if (sectionKey === "dms") return rowType === "dm";
  return sectionKey === "starred";
}

/** Rows can be put in order by hand (not in Direct messages, which follow recent activity, nor Agents). */
export function sectionSortable(sectionKey: string): boolean {
  return sectionKey === "starred" || sectionKey === "channels" || sectionKey.startsWith("f:");
}

/** A rank strictly between two ranks (either end open); bad or out-of-order bounds append after `before`. */
export function rankBetween(before: string | undefined, after: string | undefined): string {
  try {
    if (before !== undefined && after !== undefined && cmpRank(before, after) >= 0) return generateKeyBetween(before, null);
    return generateKeyBetween(before ?? null, after ?? null);
  } catch {
    return `${before ?? "a0"}V`;
  }
}

function ranksAfter(before: string | undefined, n: number): string[] {
  try {
    return generateNKeysBetween(before ?? null, null, n);
  } catch {
    const out: string[] = [];
    let prev = before;
    for (let i = 0; i < n; i++) {
      prev = rankBetween(prev, undefined);
      out.push(prev);
    }
    return out;
  }
}

/** The sidebar item a row stands for (agents are not placeable). */
function itemOf(row: SidebarEntry): { kind: SidebarItemKind; id: string } | undefined {
  if (row.type === "channel") return { kind: "channel", id: row.channelId };
  if (row.type === "dm") return { kind: "dm", id: row.channelId };
  if (row.type === "page") return { kind: "page", id: row.pageId };
  return undefined;
}

function placement(sectionKey: string, item: { kind: SidebarItemKind; id: string }): SidebarMove {
  if (sectionKey === "starred") return { kind: item.kind, id: item.id, starred: true };
  if (sectionKey.startsWith("f:")) return { kind: item.kind, id: item.id, folderId: sectionKey.slice(2) };
  return { kind: item.kind, id: item.id };
}

type Found = { section: string; index: number; row: SidebarEntry };

/** The row in the sidebar, or a docs item dragged in from the page tree ("p:<pageId>"). */
function findRow(sections: SidebarSection[], rowId: string): Found | undefined {
  for (const section of sections) {
    const index = section.rows.findIndex((r) => r.id === rowId);
    if (index >= 0) return { section: section.id, index, row: section.rows[index]! };
  }
  if (!rowId.startsWith("p:")) return undefined;
  return { section: DOCS_TREE, index: -1, row: { type: "page", id: rowId, pageId: rowId.slice(2), name: "", pageKind: "page" } };
}

export type SidebarDrop = {
  /** SidebarRow.id of the dragged item ("p:<pageId>" for a docs item from the page tree). */
  rowId: string;
  /** SidebarSection.id it was dropped in ("starred" works even while Starred is empty and not shown). */
  toSection: string;
  /** Its position in that section once dropped. */
  toIndex: number;
};

/**
 * The placement writes for a drop: the moved item (its section and, where order is manual, a rank between its new
 * neighbours) — plus, when it lands below rows still in default order, ranks for those rows so the section keeps
 * exactly the order shown. Empty for a no-op, an unknown row, an agent row or a section that doesn't take the row.
 */
export function planSidebarMove(sections: SidebarSection[], state: WorkspaceCache, drop: SidebarDrop): SidebarMove[] {
  const found = findRow(sections, drop.rowId);
  const item = found === undefined ? undefined : itemOf(found.row);
  if (found === undefined || item === undefined || !sectionAccepts(drop.toSection, found.row.type)) return [];
  if (item.kind !== "page" && state.memberships[item.id] === undefined) return [];
  const target = sections.find((s) => s.id === drop.toSection);
  const others = (target === undefined ? [] : target.rows).filter((r) => r.id !== drop.rowId);
  const at = Math.max(0, Math.min(drop.toIndex, others.length));
  const same = found.section === drop.toSection;
  if (same && (at === found.index || !sectionSortable(drop.toSection))) return [];
  const move = placement(drop.toSection, item);
  if (!sectionSortable(drop.toSection)) return [move];
  const items = state.sidebarItems ?? {};
  const rankOf = (r: SidebarEntry): string | undefined => {
    const it = itemOf(r);
    return it === undefined ? undefined : items[it.id]?.rank;
  };
  let ranked = 0;
  while (ranked < others.length && rankOf(others[ranked]!) !== undefined) ranked++;
  if (at <= ranked) {
    const before = at > 0 ? rankOf(others[at - 1]!) : undefined;
    const after = at < ranked ? rankOf(others[at]!) : undefined;
    move.rank = rankBetween(before, after);
    return [move];
  }
  // Dropped below rows in default order: rank them (keeping where they are) and then the moved row.
  const keys = ranksAfter(ranked > 0 ? rankOf(others[ranked - 1]!) : undefined, at - ranked + 1);
  const out: SidebarMove[] = [];
  for (let i = ranked; i < at; i++) {
    const it = itemOf(others[i]!);
    if (it === undefined) continue;
    out.push({ ...placement(drop.toSection, it), rank: keys[i - ranked]! });
  }
  move.rank = keys[at - ranked]!;
  out.push(move);
  return out;
}

/**
 * "Move to folder…" (keyboard and touch): the item joins `toSection` in default order (after the rows ordered by
 * hand); DOCS_TREE takes a docs item out of its folder. Empty when it is already there or the section doesn't take it.
 */
export function planMoveToSection(sections: SidebarSection[], state: WorkspaceCache, rowId: string, toSection: string): SidebarMove[] {
  const found = findRow(sections, rowId);
  const item = found === undefined ? undefined : itemOf(found.row);
  if (found === undefined || item === undefined || found.section === toSection || !sectionAccepts(toSection, found.row.type)) return [];
  if (item.kind !== "page" && state.memberships[item.id] === undefined) return [];
  return [placement(toSection, item)];
}

export type SectionTarget = { id: string; title: MessageDescriptor; emoji?: string; icon?: string };

/** Where a row can be moved: Starred, my folders, and its default section or the page tree — minus where it is. */
export function moveTargets(state: WorkspaceCache, sections: SidebarSection[], rowId: string): SectionTarget[] {
  const found = findRow(sections, rowId);
  if (found === undefined || itemOf(found.row) === undefined) return [];
  const type = found.row.type;
  const out: SectionTarget[] = [];
  const add = (t: SectionTarget) => {
    if (t.id !== found.section && sectionAccepts(t.id, type)) out.push(t);
  };
  add({ id: "starred", title: msg("nav.starred") });
  for (const f of folders(state)) {
    const t: SectionTarget = { id: folderKey(f.id), title: msg("sidebar.folder.name", { name: f.name }) };
    if (f.emoji !== undefined && f.emoji !== "") t.emoji = f.emoji;
    if (f.icon !== undefined && f.icon !== "") t.icon = f.icon;
    add(t);
  }
  add({ id: "channels", title: msg("nav.channels") });
  add({ id: "dms", title: msg("nav.directMessages") });
  add({ id: DOCS_TREE, title: msg("sidebar.move.toTree") });
  return out;
}

/** Rank for a new folder: after my last one. */
export function nextFolderRank(state: WorkspaceCache): string {
  const list = folders(state);
  return rankBetween(list.length === 0 ? undefined : list[list.length - 1]!.rank, undefined);
}

// ---------- Optimistic edits (re-applied on top of server data while still pending) ----------

/** My settings for one conversation, applied locally as the server will (starring takes it out of its folder). */
export function applyMembershipPatch(state: WorkspaceCache, channelId: string, patch: UpdateMembershipRequest): WorkspaceCache {
  const ms = state.memberships[channelId];
  if (ms === undefined) return state;
  const next: ChannelMembership = { ...ms };
  if (patch.muted !== undefined) next.muted = patch.muted;
  if (patch.notify !== undefined) next.notify = patch.notify;
  // Optimistic close time: the newest message (the server's answer brings the real time; either hides it until a newer one).
  if (patch.closed === true && next.closedAt === undefined) next.closedAt = state.channels[channelId]?.lastMessageAt ?? 0;
  if (patch.closed === false) delete next.closedAt;
  let out = state;
  if (patch.starred !== undefined && patch.starred !== ms.starred) {
    next.starred = patch.starred;
    const item = (state.sidebarItems ?? {})[channelId];
    if (item !== undefined) out = setSidebarItem(out, { kind: item.kind, id: item.id });
  }
  return setMembership(out, next);
}

/** Drag-and-drop results applied locally. */
export function applySidebarMoves(state: WorkspaceCache, moves: SidebarMove[]): WorkspaceCache {
  let next = state;
  for (const m of moves) {
    const starred = m.kind !== "page" && m.starred === true;
    if (m.kind !== "page") {
      const ms = next.memberships[m.id];
      if (ms === undefined) continue;
      if (ms.starred !== starred) next = setMembership(next, { ...ms, starred });
    }
    const item: SidebarItem = { kind: m.kind, id: m.id };
    if (!starred && m.folderId !== undefined) item.folderId = m.folderId;
    if (m.rank !== undefined && (m.kind !== "page" || item.folderId !== undefined)) item.rank = m.rank;
    next = setSidebarItem(next, item);
  }
  return next;
}

/** A folder edit applied locally (an empty emoji / icon clears it). */
export function patchSidebarFolder(state: WorkspaceCache, folderId: string, req: UpdateSidebarFolderRequest): WorkspaceCache {
  const f = (state.sidebarFolders ?? {})[folderId];
  if (f === undefined) return state;
  const next: SidebarFolder = { ...f };
  if (req.name !== undefined && req.name.trim() !== "") next.name = req.name.trim();
  if (req.emoji !== undefined) {
    if (req.emoji === "") delete next.emoji;
    else next.emoji = req.emoji;
  }
  if (req.icon !== undefined) {
    if (req.icon === "") delete next.icon;
    else next.icon = req.icon;
  }
  if (req.rank !== undefined) next.rank = req.rank;
  if (req.collapsed !== undefined) next.collapsed = req.collapsed;
  return upsertSidebarFolder(state, next);
}
